import CardIssuer from "enums/CardIssuer";
import { corpayCardClient } from "utils/cards/corpayCardClient";
import { highnoteCardClient } from "utils/cards/highnoteCardClient";
import { stripeCardClient } from "utils/cards/stripeCardClient";

const CARD_TOKEN_TYPES = [
  "STRIPE_EPHEMERAL_KEY",
  "HIGHNOTE_CLIENT_TOKEN",
  "OUTPAVE_REVEAL_SECRET",
] as const;
const OPTIONAL_TOKEN_MEMBERS = [
  "expiresAt",
  "issuerAccountId",
  "publishableKey",
];
const CARD_ISSUER_CLIENTS: Record<CardTokenType, CardIssuerClient> = {
  STRIPE_EPHEMERAL_KEY: stripeCardClient,
  HIGHNOTE_CLIENT_TOKEN: highnoteCardClient,
  OUTPAVE_REVEAL_SECRET: corpayCardClient,
};
// An issuer with no display SDK of its own, such as Corpay, is shown by our card frame from a reveal secret.
const CARD_TOKEN_TYPE_BY_ISSUER: Partial<Record<CardIssuer, CardTokenType>> = {
  [CardIssuer.STRIPE]: "STRIPE_EPHEMERAL_KEY",
  [CardIssuer.HIGHNOTE]: "HIGHNOTE_CLIENT_TOKEN",
  [CardIssuer.CORPAY]: "OUTPAVE_REVEAL_SECRET",
};

export interface StyleOptions {
  base?: {
    iconColor?: string;
    color?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontSize?: string;
    lineHeight?: string;
    letterSpacing?: string;
    fontSmoothing?: string;
    ":-webkit-autofill"?: {
      color?: string;
    };
    "::placeholder"?: {
      color?: string;
    };
  };
  invalid?: {
    iconColor?: string;
    color?: string;
  };
}

export interface CardElementOptions {
  style: StyleOptions;
}

/** The `cardToken` of `GET /api/v2/cards/{cardId}?extend=cardToken`: the one shape every card display takes. */
export interface CardToken {
  type: CardTokenType;
  issuerId: string;
  secret: string;
  expiresAt?: string;
  issuerAccountId?: string;
  publishableKey?: string;
}

export type CardTokenType = (typeof CARD_TOKEN_TYPES)[number];

/** The app's ephemeral card key and the v1 `cardToken`, which `toCardToken` reads. */
export interface LegacyCardKey {
  issuerId: string;
  accountId?: string | null;
  publishableKey?: string | null;
  /** The issuer of the card's program; a key minted before card programs names none. */
  cardIssuer?: CardIssuer | null;
  key: { id?: string | null; secret?: string | null };
}

export interface CardFieldSelectors {
  cardNumber: string;
  cvv: string;
  expirationDate: string;
}

export type CardViewerField = keyof CardFieldSelectors;

export interface CardViewerSelectors extends CardFieldSelectors {
  /** The slot an issuer with its own copy button draws it in. */
  copyCardNumber: string;
}

export interface RenderCardTokenOptions {
  cardToken: CardToken;
  selectors: CardViewerSelectors;
  style: StyleOptions;
  onCopy: (field: CardViewerField) => void;
  /** A failure the issuer reports after the fields showed. */
  onLateError: (error: unknown) => void;
  /** False once the reader left this card, so nothing more is drawn. */
  isCurrent?: () => boolean;
  /** Each field's accessible name in the reader's language, for a client that names its own frames. */
  labels: Record<CardViewerField, string>;
}

/** What a card's issuer draws besides its fields, which decides the slots a surface gives it. */
export interface CardIssuerFeatures {
  /** Draws a copy button in `copyCardNumber`; an issuer without one copies a field on click, so nothing may cover the number. */
  copyButton: boolean;
}

/** One issuer's card display: its renderer and the UI it supports. */
export interface CardIssuerClient {
  features: CardIssuerFeatures;
  /**
   * Draws a card's number, expiry and CVV with the issuer's own display
   * elements, so the details never pass through this page's DOM. Resolves true
   * once they show, false when the reader left first.
   */
  render: (options: RenderCardTokenOptions) => Promise<boolean>;
}

/** The toast for a copied field, worded as each issuer's copy control always was. */
export const getCardCopyMessage = (
  field: CardViewerField,
  t: (key: string) => string,
) =>
  field === "cardNumber"
    ? t("Card number copied to clipboard")
    : `${field === "cvv" ? t("CVV") : t("Expiration date")} ${t("copied to clipboard")}`;

/** Each field's accessible name, translated. */
export const getCardFieldLabels = (
  t: (key: string) => string,
): Record<CardViewerField, string> => ({
  cardNumber: t("Card number"),
  cvv: t("CVV"),
  expirationDate: t("Expiration date"),
});

/** True for a v2 `cardToken`, whatever members a later API version adds to it. */
export const isCardToken = (value: unknown): value is CardToken => {
  if (typeof value !== "object" || value === null) return false;
  const token: Record<string, unknown> = { ...value };
  return (
    CARD_TOKEN_TYPES.some((type) => type === token.type) &&
    isFilled(token.issuerId) &&
    isFilled(token.secret) &&
    OPTIONAL_TOKEN_MEMBERS.every(
      (member) => token[member] === undefined || isFilled(token[member]),
    )
  );
};

/** Reads the app's card key as a v2 card token of the issuer it names; a key naming none is a Stripe key when it has a key ID. */
export const toCardToken = ({
  issuerId,
  accountId,
  publishableKey,
  cardIssuer,
  key,
}: LegacyCardKey): CardToken => {
  if (!key.secret) throw new Error("The card key has no secret.");
  return {
    type: cardIssuer
      ? getCardTokenType(cardIssuer)
      : key.id
        ? "STRIPE_EPHEMERAL_KEY"
        : "HIGHNOTE_CLIENT_TOKEN",
    issuerId,
    secret: key.secret,
    ...(accountId && { issuerAccountId: accountId }),
    ...(publishableKey && { publishableKey }),
  };
};

/** The display for one card, chosen by its token's own type, since a platform can issue through several issuers. */
export const getCardIssuerClient = ({
  type,
}: Pick<CardToken, "type">): CardIssuerClient => CARD_ISSUER_CLIENTS[type];

const getCardTokenType = (cardIssuer: CardIssuer): CardTokenType => {
  const type = CARD_TOKEN_TYPE_BY_ISSUER[cardIssuer];
  if (!type) throw new Error(`A ${cardIssuer} card has no card token.`);
  return type;
};

const isFilled = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;
